import { type Auftrag, type AuftragsTyp, type GesamtUmfang, istLoeschbar } from '@awf/shared';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { loescheAuftrag } from '../api/client.js';
import { useAuth } from '../auth/auth-context.js';
import { AUFTRAGSTYPEN, hatFreigabeSeite, TYP_REIHENFOLGE, typName } from '../auftragstypen.js';
import { alsDatum, alsDatumZeit } from '../format.js';
import seite from '../ui/seite.module.css';
import { ErrorState, LoadingState } from '../ui/states.js';
import { statusText } from '../ui/status-chip.js';
import { LinienSymbol, type LinienSymbolName } from '../ui/linien-symbol.js';
import { HOL_GRENZE, useAuftraegeKontext, useGesamtAuftraegeKontext } from './auftraege-kontext.js';
import { Banner, BANNER_BILD } from './banner.js';
import { angelegtVon, istFremd, istSichtbar } from './einsicht.js';
import { istFrischFertig, zaehleKennzahlen } from './kennzahlen.js';
import {
  alsMonat,
  KONTINGENT_TEXT,
  monatVon,
  NUTZUNGSKONTINGENT,
  nutzungNachMonat,
  vorMonat,
} from './nutzung.js';
import { KontingentBalken, NutzungsStatistik } from './nutzungs-statistik.js';
import stile from './run-list-page.module.css';
import { anzeigeStatus } from './uebergabe.js';
import type { AuftragsZustand } from './use-runs.js';
import {
  GRUPPEN_TEXT,
  gruppeVon,
  sortiereAuftraege,
  type StatusGruppe,
  TYP_TEXT,
} from './status.js';
import { useWartendeKontext, WARTENDE_GRENZE } from './wartende-kontext.js';

/**
 * Zwei Grenzen, und ihr Unterschied ist der Kern von Befund H2 des Codex-Retro-Reviews.
 *
 * Vorher gab es nur eine: Firestore lieferte 50 Auftraege, und Priorisierung, Suche und
 * Filterung liefen erst DANACH im Client. Die Suche durchsuchte damit den Ausschnitt statt
 * den Bestand und meldete "Kein Auftrag passt", obwohl es einen gab; ein aelterer wartender
 * Lauf konnte schlicht fehlen.
 *
 * WAS DIESE SEITE EINLOEST -- und was nicht. Das ist keine Notloesung, sondern der ZUSCHNITT,
 * den der Auftraggeber am 2026-09-14 entschieden hat, nachdem die technische Grenze auf dem
 * Tisch lag:
 *
 * - WARTENDE Laeufe sind unabhaengig vom Alter abgedeckt, bis zu `WARTENDE_GRENZE`. Dafuer
 *   gibt es die eigene Abfrage `beobachteWartende`, die SERVERSEITIG auf den Status filtert.
 *   Das ist der Fall, der weh tut: Auf einen wartenden Lauf wartet ein Mensch.
 * - Suche und Filter decken die zuletzt angelegten `HOL_GRENZE` Laeufe ab, NICHT den
 *   gesamten Bestand. Ein aelterer, nicht wartender Lauf dahinter ist hier nicht auffindbar.
 *   Die Oberflaeche sagt das, sobald die Grenze greift.
 *
 * Warum nicht mehr: Firestore kann keine Teilstring-Suche. Eine Suche ueber den gesamten
 * Bestand braucht einen Suchindex im Backend -- ein eigenes Vorhaben mit eigener Spec, nicht
 * der Rand dieses Befunds. Ohne jede Grenze wuerde stattdessen jeder Seitenaufruf alles
 * ziehen.
 *
 * Die urspruengliche Formulierung des Befunds H2 lautete "Suche ueber alles". Sie ist mit
 * dieser Entscheidung bewusst enger gefasst; wer sie wieder aufmacht, braucht den
 * Backend-Suchindex, nicht eine groessere Zahl. Eine groessere Zahl verschiebt nur.
 */
export const ZEIG_GRENZE = 50;
export { HOL_GRENZE } from './auftraege-kontext.js';

// Die Grenze lebt beim geteilten Listener; hier nur weitergereicht, damit bestehende
// Importe (Tests, Hinweis am Deckel der Wartenden) halten.
export { WARTENDE_GRENZE };

const GRUPPEN: (StatusGruppe | 'alle')[] = ['alle', 'wartet', 'laeuft', 'fertig', 'fehler'];
const TYPEN: readonly (AuftragsTyp | 'alle')[] = ['alle', ...TYP_REIHENFOLGE];

// Die Adresse ist Eingabe von aussen: Ein Tippfehler oder ein alter Link darf die Liste
// nicht leer filtern, sondern faellt auf "alle" zurueck.
function gruppeAus(wert: string | null): StatusGruppe | 'alle' {
  return GRUPPEN.find((g) => g === wert) ?? 'alle';
}

function typAus(wert: string | null): AuftragsTyp | 'alle' {
  return TYPEN.find((t) => t === wert) ?? 'alle';
}

/** Einziger Zeitraum: die letzten 7 Tage der Karte "Fertig". Andere Werte gelten als keiner. */
const SIEBEN_TAGE = '7t';

function zeitraumAus(wert: string | null): typeof SIEBEN_TAGE | null {
  return wert === SIEBEN_TAGE ? SIEBEN_TAGE : null;
}

/**
 * Zeitraum-Auswahl wie in der Content-Factory (`ZEITRAEUME`), bezogen auf das Anlagedatum.
 * Unabhaengig vom Zeitraum der Karte "Fertig, letzte 7 Tage", der am Fertigwerden haengt.
 */
const SEIT = ['alle', 'heute', '7-tage', '30-tage'] as const;
type Seit = (typeof SEIT)[number];
const SEIT_TEXT: Record<Seit, string> = {
  alle: 'Jederzeit',
  heute: 'Heute',
  '7-tage': 'Letzte 7 Tage',
  '30-tage': 'Letzte 30 Tage',
};
const TAG_MS = 24 * 60 * 60 * 1000;

function imZeitraum(angelegtAm: string, seit: Seit, jetzt: Date): boolean {
  if (seit === 'alle') return true;
  const zeitpunkt = Date.parse(angelegtAm);
  if (Number.isNaN(zeitpunkt)) return false;
  if (seit === 'heute') return new Date(zeitpunkt).toDateString() === jetzt.toDateString();
  const tage = seit === '7-tage' ? 7 : 30;
  return jetzt.getTime() - zeitpunkt <= tage * TAG_MS;
}

/** Status mit Symbol UND Text wie in der Content-Factory, nie Farbe allein. */
const STATUS_SYMBOL: Record<StatusGruppe, LinienSymbolName> = {
  wartet: 'clock',
  laeuft: 'refresh',
  fertig: 'check',
  fehler: 'alert',
};

/** Die Kennzahlen altern mit, solange die Liste offen ist: lokaler Takt, kein Netzverkehr. */
const KENNZAHL_TAKT_MS = 60_000;

const SEKUNDENGENAU = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

function passt(
  auftrag: Auftrag,
  gruppe: StatusGruppe | 'alle',
  typ: AuftragsTyp | 'alle',
  suche: string,
  nurSiebenTage: Date | null,
  seit: Seit,
  jetzt: Date,
  mitBesitzer: boolean,
  person: string | null,
): boolean {
  if (gruppe !== 'alle' && gruppeVon(auftrag.status) !== gruppe) return false;
  if (person !== null && auftrag.ownerUid !== person) return false;
  if (!imZeitraum(auftrag.angelegtAm, seit, jetzt)) return false;
  // Dasselbe Praedikat wie die Kennzahl: Zahl und Liste stimmen damit ueberein.
  if (nurSiebenTage && !istFrischFertig(auftrag, nurSiebenTage)) return false;
  if (typ !== 'alle' && auftrag.typ !== typ) return false;
  if (!suche) return true;
  const nadel = suche.toLowerCase();
  // In der Gesamtansicht auch nach der Person: "Wer hat das angelegt?" ist dort die naheliegende
  // Frage. In der eigenen Liste traefe das jede Zeile.
  const besitzer = mitBesitzer
    ? [auftrag.ownerName, auftrag.ownerEmail].some((w) => w?.toLowerCase().includes(nadel))
    : false;
  return (
    besitzer ||
    auftrag.thema.toLowerCase().includes(nadel) ||
    auftrag.runId.toLowerCase().includes(nadel)
  );
}

/**
 * Die Gesamtansicht steht in der Adresse wie die Filter, damit ein Neuladen sie behaelt. Ohne
 * Rolle gilt `?ansicht=gesamt` nicht: Die Abfrage scheiterte an den Rules, und die Seite zeigte
 * einen Fehler statt der eigenen Auftraege.
 */
type Ansicht = 'eigene' | 'gesamt';

function ansichtAus(wert: string | null, umfang: GesamtUmfang | null): Ansicht {
  return umfang !== null && wert === 'gesamt' ? 'gesamt' : 'eigene';
}

interface Person {
  uid: string;
  text: string;
}

/**
 * Auswahl "Angelegt von" der Gesamtansicht: jede Person, die im geladenen Bestand vorkommt,
 * einmal, unter demselben Namen wie in der Spalte. Gefiltert wird nach uid, nicht nach dem
 * Namen -- zwei Personen ohne Anzeigenamen koennen gleich heissen. "Sie" steht vorn.
 */
function personenAus(auftraege: readonly Auftrag[], uid: string | null): Person[] {
  const nachUid = new Map<string, string>();
  for (const a of auftraege) {
    if (!nachUid.has(a.ownerUid)) nachUid.set(a.ownerUid, angelegtVon(a, uid));
  }
  return [...nachUid]
    .map(([u, text]) => ({ uid: u, text }))
    .sort((x, y) => (x.uid === uid ? -1 : y.uid === uid ? 1 : x.text.localeCompare(y.text, 'de')));
}

/** `admin` sieht nur ERGO, `technik` alles -- die Beschriftung sagt, welche der beiden es ist. */
const GESAMT_TEXT: Record<GesamtUmfang, string> = {
  ergo: 'Alle ERGO-Aufträge',
  alle: 'Alle Aufträge',
};

/**
 * Stabil gehalten: Die Liste haengt Effekte an diesen Zustand. In der Gesamtansicht gibt es keine
 * Wartenden-Abfrage (siehe `beobachteGesamt`), die eigenen wartenden Laeufe gehoeren nicht hinein.
 */
const KEINE_WARTENDEN: AuftragsZustand = { art: 'daten', auftraege: [] };

export interface RunListPageProps {
  /** Austauschbar fuer Tests; im Betrieb der Aufruf an `DELETE /runs/:id`. */
  loesche?: (runId: string) => Promise<void>;
}

/**
 * Geloescht werden nur abgeschlossene Auftraege, wie im Backend (`loeschHandler`): Ein laufender
 * arbeitet in n8n weiter und legte sich mit seiner naechsten Meldung als verwaistes Dokument
 * wieder an (lokaler Codex-Review PR #70).
 */
/** Dieselbe Regel wie im Backend (packages/shared): abgeschlossen oder stehen geblieben. */
function loeschbar(auftrag: Auftrag, jetzt: Date): boolean {
  return istLoeschbar(auftrag, jetzt.getTime());
}

function anzahlAuftraege(anzahl: number): string {
  return anzahl === 1 ? '1 Auftrag' : `${anzahl} Aufträge`;
}

export function RunListPage({ loesche = loescheAuftrag }: RunListPageProps = {}) {
  const { nutzer } = useAuth();
  const uid = nutzer?.uid ?? null;
  const umfang = nutzer?.gesamtUmfang ?? null;

  // Gruppe und Typ stehen in der Adresse, damit die Seitenleiste auf gefilterte Ansichten
  // zeigen kann ("Ratgeber > Review") und ein Wechsel zwischen ihnen die gemountete Liste
  // mitnimmt. `replace`, damit jeder Klick auf einen Filter keinen Verlaufseintrag erzeugt.
  const [parameter, setParameter] = useSearchParams();
  const ansicht = ansichtAus(parameter.get('ansicht'), umfang);
  const gesamt = ansicht === 'gesamt';
  const eigeneZustand = useAuftraegeKontext();
  const gesamtZustand = useGesamtAuftraegeKontext(gesamt);
  const zustand = gesamt ? gesamtZustand : eigeneZustand;
  const setzeAnsicht = (neu: Ansicht) =>
    setParameter(
      (vorher) => {
        const p = new URLSearchParams(vorher);
        if (neu === 'gesamt') p.set('ansicht', 'gesamt');
        else p.delete('ansicht');
        // Die Personenwahl gehoert zur Gesamtansicht; in der eigenen Liste gibt es nur eine.
        p.delete('person');
        return p;
      },
      { replace: true },
    );
  const gruppe = gruppeAus(parameter.get('gruppe'));
  const typ = typAus(parameter.get('typ'));
  // Der Zeitraum gehoert zur Karte "Fertig": Nur zusammen mit `gruppe=fertig` gilt er. Eine
  // handgebaute Adresse wie `?gruppe=laeuft&zeitraum=7t` zeigte sonst eine leere Liste mit
  // Hinweis (Codex-Gate PR #56).
  const zeitraum = gruppe === 'fertig' ? zeitraumAus(parameter.get('zeitraum')) : null;
  // Jede Wahl einer Statusgruppe (Segment oder Karte) hebt den Zeitraum auf; nur die Karte
  // "Fertig, letzte 7 Tage" setzt ihn wieder. Ein Typwechsel laesst ihn stehen.
  const setzeFilter = (schluessel: 'gruppe' | 'typ', wert: string, mitZeitraum = false) =>
    setParameter(
      (vorher) => {
        const neu = new URLSearchParams(vorher);
        if (wert === 'alle') neu.delete(schluessel);
        else neu.set(schluessel, wert);
        if (schluessel === 'gruppe') {
          if (mitZeitraum) neu.set('zeitraum', SIEBEN_TAGE);
          else neu.delete('zeitraum');
        }
        return neu;
      },
      { replace: true },
    );
  // Eigener Weg statt `setzeFilter`: Dort steht "alle" fuer keinen Filter, eine uid ist aber
  // freier Text und koennte so heissen (Codex-Gate PR #89). "Alle Personen" ist deshalb der
  // leere Wert, den es als uid nicht gibt.
  const setzePerson = (wert: string) =>
    setParameter(
      (vorher) => {
        const neu = new URLSearchParams(vorher);
        if (wert === '') neu.delete('person');
        else neu.set('person', wert);
        return neu;
      },
      { replace: true },
    );
  const hebeZeitraumAuf = () =>
    setParameter(
      (vorher) => {
        const neu = new URLSearchParams(vorher);
        neu.delete('zeitraum');
        return neu;
      },
      { replace: true },
    );
  const [jetzt, setJetzt] = useState(() => new Date());
  useEffect(() => {
    const takt = setInterval(() => setJetzt(new Date()), KENNZAHL_TAKT_MS);
    return () => clearInterval(takt);
  }, []);
  const [suche, setSuche] = useState('');
  const [seit, setSeit] = useState<Seit>('alle');

  // Die wartenden Laeufe kommen aus dem geteilten Listener ueber der Shell. Die Hauptabfrage
  // schneidet in Firestore nach Alter ab, BEVOR hier priorisiert wird -- ein wartender Lauf
  // jenseits der Hol-Grenze fehlt sonst vollstaendig, obwohl auf ihn ein Mensch wartet.
  const eigeneWartende = useWartendeKontext();
  const wartendeZustand = gesamt ? KEINE_WARTENDEN : eigeneWartende;

  const geholt = zustand.art === 'daten' ? zustand.auftraege : [];
  const wartende = wartendeZustand.art === 'daten' ? wartendeZustand.auftraege : [];

  // Geladen ist erst, wenn BEIDE Abfragen geantwortet haben. Sonst stuende "Kein Auftrag
  // passt" auf der Seite, waehrend die wartenden Laeufe noch unterwegs sind -- also die
  // Verwechslung von leer und noch-nicht-da, die Abnahmekriterium 8 ausschliesst.
  const laedtNoch = zustand.art === 'laedt' || wartendeZustand.art === 'laedt';

  // Nach runId zusammengefuehrt: Ein wartender Lauf innerhalb der Hol-Grenze kommt aus
  // beiden Abfragen und darf trotzdem nur einmal in der Liste stehen.
  const bestand = useMemo(() => {
    const nachId = new Map(geholt.map((a) => [a.runId, a]));
    // Der JUENGERE Stand gewinnt, nicht die Wartenden-Abfrage pauschal: Zieht ein Lauf weiter,
    // meldet die Hauptabfrage das sofort, die Wartenden-Abfrage kann noch den alten Stand
    // tragen. Bei Gleichstand bleibt der Eintrag der Hauptabfrage.
    for (const a of wartende) {
      const vorhanden = nachId.get(a.runId);
      if (!vorhanden || Date.parse(a.aktualisiertAm) > Date.parse(vorhanden.aktualisiertAm))
        nachId.set(a.runId, a);
    }
    return [...nachId.values()];
  }, [geholt, wartende]);
  // Was die Liste zeigt; `bestand` mit den ausgeblendeten zaehlt nur die Nutzung (siehe `istSichtbar`).
  const alle = useMemo(() => bestand.filter(istSichtbar), [bestand]);
  // Die Nutzung zaehlt NUR die Hauptabfrage samt ausgeblendeten (Codex-Gate PR #91): Ihr Deckel
  // bestimmt, ab welchem Monat die Zahlen Untergrenzen sind. Mischte man aeltere Treffer anderer
  // Abfragen hinein, verschoebe sich dieser Grenzmonat nach hinten, und neuere, angeschnittene
  // Monate gaelten als vollstaendig. In der Gesamtansicht gibt es heute keine zweite Abfrage, die
  // Trennung haelt das auch fuer spaeter fest.
  const nutzungsBestand = geholt;
  // Personen der Statistik: auch wer nur noch ausgeblendete Auftraege hat, hat genutzt.
  const statistikPersonen = useMemo(
    () => (gesamt ? personenAus(nutzungsBestand, uid) : []),
    [gesamt, nutzungsBestand, uid],
  );

  // Wie Gruppe und Typ in der Adresse. Eine uid, die im Bestand nicht (mehr) vorkommt -- alter
  // Link, letzter Auftrag der Person geloescht --, faellt auf "alle" zurueck, statt die Liste
  // leer zu filtern und eine Auswahl zu zeigen, die es nicht gibt.
  const personen = useMemo(() => (gesamt ? personenAus(alle, uid) : []), [gesamt, alle, uid]);
  const gewuenschtePerson = parameter.get('person');
  const person = personen.some((p) => p.uid === gewuenschtePerson) ? gewuenschtePerson : null;

  // Wann kam zuletzt etwas von Firestore? Ohne diese Anzeige laesst sich "die Liste
  // aktualisiert sich nicht" nicht von "es hat sich nichts geaendert" unterscheiden --
  // und ein gedrosselter Hintergrund-Tab sieht aus wie ein Fehler (Befund der
  // Live-Abnahme vom 2026-09-10).
  // Beide Zustaende zaehlen: Ein Lauf, der nur ueber die Wartenden-Abfrage dazukommt oder
  // verschwindet, aendert die Liste sehr wohl -- ein Zeitstempel, der dabei stehen bleibt,
  // sieht aus wie eine abgerissene Verbindung.
  //
  // GETRENNTE Effekte, nicht einer mit Oder: In einem gemeinsamen Effekt wuerde jeder
  // Zustandswechsel den Zeitstempel setzen, solange der andere Zustand `daten` ist -- auch
  // ein Fehler oder ein Wiederholungsversuch, bei dem gar kein Snapshot eintraf. Der
  // Zeitstempel behauptet dann Aktualitaet, die es nicht gibt.
  const [stand, setStand] = useState<Date | null>(null);
  useEffect(() => {
    if (zustand.art === 'daten') setStand(new Date());
  }, [zustand]);
  useEffect(() => {
    if (wartendeZustand.art === 'daten') setStand(new Date());
  }, [wartendeZustand]);
  // Sobald gesucht oder gefiltert wird, zaehlt der Treffer, nicht die Position: Wer nach
  // "Reise" sucht, will den Lauf sehen, auch wenn er der 700. ist. Ohne Einschraenkung bleibt
  // es beim Ausschnitt, damit die erste Ansicht nicht 1000 Zeilen rendert.
  const eingeschraenkt =
    gruppe !== 'alle' ||
    typ !== 'alle' ||
    suche !== '' ||
    zeitraum !== null ||
    seit !== 'alle' ||
    person !== null;
  const nurSiebenTage = zeitraum === SIEBEN_TAGE ? jetzt : null;
  const treffer = useMemo(
    () =>
      sortiereAuftraege(
        alle.filter((a) =>
          passt(a, gruppe, typ, suche, nurSiebenTage, seit, jetzt, gesamt, person),
        ),
      ),
    [alle, gruppe, typ, suche, nurSiebenTage, seit, jetzt, gesamt, person],
  );
  const zeilen = eingeschraenkt ? treffer : treffer.slice(0, ZEIG_GRENZE);
  const amDeckel = geholt.length >= HOL_GRENZE;
  const wartendeAmDeckel = wartende.length >= WARTENDE_GRENZE;
  // Fremde Auftraege gibt es nur in der Gesamtansicht; geloescht und entschieden wird nur, was
  // einem selbst gehoert (das Backend weist alles andere ohnehin ab).
  const fremd = (a: Auftrag) => gesamt && istFremd(a, uid);
  const loeschbarHier = (a: Auftrag) => loeschbar(a, jetzt) && !fremd(a);

  // Loeschen wie in der Content-Factory (uebersicht/auftragsliste.tsx, #1308): Auswahl je Zeile,
  // eine Leiste mit Anzahl und Knopf, dann eine Bestaetigung. Gemerkt werden nur Kennungen;
  // gezaehlt wird gegen den aktuellen Bestand, damit ein anderswo verschwundener Auftrag nicht
  // als ausgewaehlt mitzaehlt.
  const [markiert, setMarkiert] = useState<Set<string>>(new Set());
  const [zeigeBestaetigung, setZeigeBestaetigung] = useState(false);
  const [loescht, setLoescht] = useState(false);
  const [fehlgeschlagen, setFehlgeschlagen] = useState<number | null>(null);
  const ausgewaehlt = useMemo(() => {
    const vorhanden = new Set(
      alle.filter((a) => loeschbar(a, jetzt) && !(gesamt && istFremd(a, uid))).map((a) => a.runId),
    );
    return new Set([...markiert].filter((id) => vorhanden.has(id)));
  }, [markiert, alle, jetzt, gesamt, uid]);
  const loeschbareZeilen = zeilen.filter(loeschbarHier);
  const alleZeilenAusgewaehlt =
    loeschbareZeilen.length > 0 && loeschbareZeilen.every((a) => ausgewaehlt.has(a.runId));

  function auswahlUmschalten(runId: string, gesetzt: boolean) {
    setMarkiert((bisher) => {
      const neu = new Set(bisher);
      if (gesetzt) neu.add(runId);
      else neu.delete(runId);
      return neu;
    });
  }

  // Wie in der Content-Factory nur die sichtbaren Zeilen: eine unter einem anderen Filter
  // getroffene Auswahl bleibt stehen.
  function alleUmschalten() {
    setMarkiert((bisher) => {
      const neu = new Set(bisher);
      for (const a of loeschbareZeilen) {
        if (alleZeilenAusgewaehlt) neu.delete(a.runId);
        else neu.add(a.runId);
      }
      return neu;
    });
  }

  async function bestaetigen() {
    const ids = [...ausgewaehlt];
    setLoescht(true);
    const ergebnisse = await Promise.allSettled(ids.map((id) => loesche(id)));
    setLoescht(false);
    // Nur die gescheiterten bleiben markiert: Ein zweiter Klick wiederholt genau diese.
    const nichtGeklappt = ids.filter((_, i) => ergebnisse[i]?.status === 'rejected');
    setMarkiert(new Set(nichtGeklappt));
    if (nichtGeklappt.length === 0) {
      setZeigeBestaetigung(false);
      setFehlgeschlagen(null);
    } else {
      setFehlgeschlagen(nichtGeklappt.length);
    }
  }

  // Ohne Auswahl ist eine offene Bestaetigung "0 Auftraege loeschen?" sinnlos.
  useEffect(() => {
    if (ausgewaehlt.size === 0 && zeigeBestaetigung && !loescht) {
      setZeigeBestaetigung(false);
      setFehlgeschlagen(null);
    }
  }, [ausgewaehlt, zeigeBestaetigung, loescht]);

  // Ohne eigene Abfrage: aus den zusammengefuehrten Daten (Spec C, Abschnitt 4 und 6).
  // Wie dem Typ folgen sie auch der Person: "Wartet auf Freigabe" heisst dann "bei ihr".
  const kennzahlen = useMemo(
    () =>
      zaehleKennzahlen(
        person === null ? alle : alle.filter((a) => a.ownerUid === person),
        typ,
        jetzt,
      ),
    [alle, person, typ, jetzt],
  );
  const kachelGewaehlt = (g: StatusGruppe) =>
    g === 'fertig' ? gruppe === 'fertig' && zeitraum === SIEBEN_TAGE : gruppe === g;
  // Exakte Zahlen, nie "mindestens" (Vorgabe 2026-10-16). Am Deckel sagen das die Hinweise
  // unter der Kopfzeile, nicht die Kacheln.
  const KARTEN = [
    { gruppe: 'laeuft', text: 'Läuft', wert: kennzahlen.laeuft },
    { gruppe: 'wartet', text: 'Wartet auf Freigabe', wert: kennzahlen.wartet },
    { gruppe: 'fertig', text: 'Fertig, letzte 7 Tage', wert: kennzahlen.fertigSiebenTage },
  ] as const;

  // Gesamtansicht: Wer sie oeffnet, fuehrt den Betrieb und fragt zuerst nach der Nutzung im Monat
  // und dem Kontingent (Vorgabe 2026-10-16). Diese Kacheln gelten uebergreifend, ungeachtet der
  // Listenfilter -- das Kontingent gilt fuer alle Workflows und Personen zusammen. Nur "Wartet auf
  // Freigabe" bleibt die Filterkachel von oben und folgt Typ und Person wie dort.
  const monatJetzt = monatVon(jetzt);
  const nutzung = useMemo(
    () =>
      gesamt ? nutzungNachMonat(nutzungsBestand, { typ: 'alle', person: null }, jetzt, 2) : [],
    [gesamt, nutzungsBestand, jetzt],
  );
  const dieserMonat = nutzung[0];
  const vormonat = nutzung[1];
  // Fehlt der Vormonat, liegt vor dem laufenden Monat schlicht kein Auftrag: dann 0.
  const vormonatSchluessel = vorMonat(monatJetzt, 1);
  const INFO_KARTEN = [
    {
      text: `Durchläufe im ${alsMonat(monatJetzt, true)}`,
      wert: dieserMonat?.durchlaeufe ?? 0,
      kontingent: dieserMonat?.durchlaeufe ?? 0,
    },
    {
      text: `Aktive Personen im ${alsMonat(monatJetzt, true)}`,
      wert: dieserMonat?.personen ?? 0,
    },
    {
      text: `Durchläufe im ${alsMonat(vormonatSchluessel, true)}`,
      wert: vormonat?.durchlaeufe ?? 0,
    },
  ];
  const filterKarten = gesamt ? KARTEN.filter((k) => k.gruppe === 'wartet') : KARTEN;

  return (
    <>
      <Banner
        bild={BANNER_BILD}
        email={nutzer?.email ?? null}
        name={nutzer?.name ?? null}
        jetzt={new Date()}
        links={
          umfang !== null && (
            <div className={stile.ansicht} role="group" aria-label="Welche Aufträge">
              <button
                type="button"
                className={stile.ansichtKnopf}
                aria-pressed={!gesamt}
                onClick={() => setzeAnsicht('eigene')}
              >
                Meine Aufträge
              </button>
              <button
                type="button"
                className={stile.ansichtKnopf}
                aria-pressed={gesamt}
                onClick={() => setzeAnsicht('gesamt')}
              >
                {GESAMT_TEXT[umfang]}
              </button>
            </div>
          )
        }
      />
      {/* Eigene Meldung statt eines stillen Ausfalls: Scheitert nur die Abfrage der wartenden
          Laeufe, verschwindet sonst spurlos genau der Lauf, auf den jemand wartet. Die
          Hauptliste bleibt dabei stehen -- ein Teilfehler nimmt nicht alles weg. */}
      {wartendeZustand.art === 'fehler' && (
        <div className="eds-alert eds-alert--warning" role="alert">
          <p>
            Aufträge mit ausstehender Freigabe konnten nicht geprüft werden. Es kann sein, dass
            einer davon hier fehlt.
          </p>
          {/* Ein beendeter Firestore-Listener startet von selbst nicht neu. Ohne diesen Weg
              bliebe nur das Neuladen der Seite. */}
          <button
            className="eds-button eds-button--secondary"
            type="button"
            onClick={wartendeZustand.wiederholen}
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {laedtNoch && <LoadingState text="Aufträge werden geladen …" />}

      {zustand.art === 'fehler' && (
        <ErrorState text={zustand.text} onWiederholen={zustand.wiederholen} />
      )}

      {/* Kacheln, Filter und Tabelle stehen, sobald die Hauptabfrage geantwortet hat, auch bei
          leerem Bestand: dann ist die Tabelle einfach leer, ohne eigene grosse Meldung
          (Auftrag 2026-10-13, wie in der Content-Factory). Leer und Fehler bleiben trotzdem
          unterscheidbar: Ein Fehler zeigt oben seine eigene Meldung. Scheitert nur die Hauptabfrage,
          tragen die wartenden Laeufe die Tabelle allein. */}
      {!laedtNoch && (zustand.art === 'daten' || alle.length > 0) && (
        <>
          {/* Bei gescheiterter Hauptabfrage waeren "Laeuft 0" und "Fertig 0" Behauptungen ueber
              einen Bestand, den niemand kennt. */}
          {zustand.art === 'daten' && (
            <>
              <div className={stile.kennzahlen} data-anzahl={gesamt ? 4 : 3}>
                {gesamt &&
                  INFO_KARTEN.map((karte) => (
                    <div key={karte.text} className={`${stile.kennzahl} ${stile.kennzahlInfo}`}>
                      <span className={stile.kennzahlText}>{karte.text}</span>{' '}
                      <span className={stile.kennzahlWert}>{karte.wert}</span>
                      {karte.kontingent !== undefined && (
                        <>
                          <span className={stile.kennzahlZusatz}>
                            von {NUTZUNGSKONTINGENT} im Kontingent
                          </span>
                          <KontingentBalken durchlaeufe={karte.kontingent} />
                        </>
                      )}
                    </div>
                  ))}
                {filterKarten.map((karte) => (
                  <button
                    key={karte.gruppe}
                    type="button"
                    className={stile.kennzahl}
                    aria-pressed={kachelGewaehlt(karte.gruppe)}
                    // Ein zweiter Klick auf die gewaehlte Kachel hebt den Filter wieder auf
                    // (Rueckmeldung 2026-10-13), wie ein Umschalter.
                    onClick={() =>
                      kachelGewaehlt(karte.gruppe)
                        ? setzeFilter('gruppe', 'alle')
                        : setzeFilter('gruppe', karte.gruppe, karte.gruppe === 'fertig')
                    }
                  >
                    <span className={stile.kennzahlText}>{karte.text}</span>{' '}
                    <span className={stile.kennzahlWert}>{karte.wert}</span>
                  </button>
                ))}
              </div>
              {/* Direkt unter den Kacheln (Rueckmeldung 2026-10-16): Der Satz erklaert deren
                Zaehlweise und das Kontingent, nicht erst die Tabelle darunter. */}
              {gesamt && (
                <p className={stile.hinweis} data-testid="zaehlweise">
                  Gezählt wird jeder angelegte Auftrag im Monat seines Anlegens. {KONTINGENT_TEXT}
                </p>
              )}
            </>
          )}

          {gesamt && zustand.art === 'daten' && (
            <NutzungsStatistik
              auftraege={nutzungsBestand}
              personen={statistikPersonen}
              jetzt={jetzt}
              gedeckelt={amDeckel}
            />
          )}

          <section className={stile.bereich} aria-labelledby="ihre-auftraege">
            <div className={stile.kopfzeile}>
              <h1 id="ihre-auftraege" className={stile.titel}>
                {gesamt && umfang ? GESAMT_TEXT[umfang] : 'Ihre Aufträge'}
              </h1>
              <span className={stile.zaehlung}>{alle.length} gesamt</span>
              {stand && (
                <small className={stile.stand} data-testid="stand">
                  Stand {SEKUNDENGENAU.format(stand)} · aktualisiert sich selbst
                </small>
              )}
            </div>

            {/* Eine Reihe wie in der Content-Factory: Suchfeld zuerst (man hat meist ein Wort im
                Kopf), dann die Kriterien, die man nicht tippen kann. */}
            <div className={stile.filterleiste}>
              <div className={stile.suchfeld} role="search">
                <span className={stile.lupe} aria-hidden="true">
                  <LinienSymbol name="search" groesse={16} />
                </span>
                <input
                  className={stile.sucheEingabe}
                  type="search"
                  placeholder="Aufträge durchsuchen"
                  aria-label="Aufträge durchsuchen, nach Thema oder Lauf-Kennung"
                  value={suche}
                  onChange={(e) => setSuche(e.target.value)}
                />
              </div>
              <select
                className={stile.auswahl}
                aria-label="Zeitraum"
                value={seit}
                onChange={(e) => setSeit(e.target.value as Seit)}
              >
                {SEIT.map((wert) => (
                  <option key={wert} value={wert}>
                    {SEIT_TEXT[wert]}
                  </option>
                ))}
              </select>
              <select
                className={stile.auswahl}
                aria-label="Typ"
                value={typ}
                onChange={(e) => setzeFilter('typ', e.target.value)}
              >
                <option value="alle">Alle Typen</option>
                {TYP_REIHENFOLGE.map((t) => (
                  <option key={t} value={t}>
                    {typName(t)}
                  </option>
                ))}
              </select>
              <select
                className={stile.auswahl}
                aria-label="Status"
                value={gruppe}
                onChange={(e) => setzeFilter('gruppe', e.target.value)}
              >
                {GRUPPEN.map((wert) => (
                  <option key={wert} value={wert}>
                    {wert === 'alle' ? 'Alle Status' : GRUPPEN_TEXT[wert]}
                  </option>
                ))}
              </select>
              {gesamt && (
                <select
                  className={stile.auswahl}
                  aria-label="Angelegt von"
                  value={person ?? ''}
                  onChange={(e) => setzePerson(e.target.value)}
                >
                  <option value="">Alle Personen</option>
                  {personen.map((p) => (
                    <option key={p.uid} value={p.uid}>
                      {p.text}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {ausgewaehlt.size > 0 && (
              <div className={stile.loeschLeiste}>
                <span>{anzahlAuftraege(ausgewaehlt.size)} ausgewählt</span>
                <button
                  type="button"
                  className={stile.loeschKnopf}
                  onClick={() => setZeigeBestaetigung(true)}
                  disabled={loescht}
                >
                  <LinienSymbol name="trash" groesse={14} />
                  Ausblenden
                </button>
              </div>
            )}

            {zeigeBestaetigung && ausgewaehlt.size > 0 && (
              <div className={`eds-alert eds-alert--warning ${stile.loeschDialog}`} role="alert">
                <p className={stile.loeschTitel}>{anzahlAuftraege(ausgewaehlt.size)} ausblenden?</p>
                {/* Ehrlich benannt (Entscheidung 2026-10-16): Endgueltig geloescht wird nicht mehr,
                    damit jeder Durchlauf in der Monatsnutzung bleibt. */}
                <p className={stile.loeschHinweis}>
                  Ausgeblendete Aufträge verschwinden aus der Übersicht, zählen aber weiter zur
                  monatlichen Nutzung.
                </p>
                <div className={stile.loeschKnoepfe}>
                  <button
                    type="button"
                    className={stile.loeschKnopf}
                    onClick={() => void bestaetigen()}
                    disabled={loescht}
                  >
                    {loescht ? 'Wird ausgeblendet …' : 'Ausblenden bestätigen'}
                  </button>
                  <button
                    type="button"
                    className={stile.abbrechenKnopf}
                    onClick={() => {
                      setZeigeBestaetigung(false);
                      setFehlgeschlagen(null);
                    }}
                    disabled={loescht}
                  >
                    Abbrechen
                  </button>
                </div>
                {fehlgeschlagen !== null && (
                  <p className={stile.loeschFehler}>
                    {fehlgeschlagen === 1
                      ? '1 Auftrag konnte nicht ausgeblendet werden. Bitte versuchen Sie es erneut.'
                      : `${fehlgeschlagen} Aufträge konnten nicht ausgeblendet werden. Bitte versuchen Sie es erneut.`}
                  </p>
                )}
              </div>
            )}

            {zeitraum === SIEBEN_TAGE && (
              <div className={stile.zeitraum}>
                <p>Nur Aufträge, die in den letzten 7 Tagen fertig wurden</p>
                <button
                  type="button"
                  className="eds-button eds-button--tertiary eds-button--sm"
                  onClick={hebeZeitraumAuf}
                >
                  Zeitraum aufheben
                </button>
              </div>
            )}

            {/* Nicht "die neuesten": `sortiereAuftraege` stellt wartende Laeufe nach vorn, und
                erst danach wird abgeschnitten. Ein alter wartender Lauf steht also im
                Ausschnitt, ein neuerer fertiger womoeglich nicht. */}
            {zeilen.length < treffer.length && (
              <p className={stile.hinweis} data-testid="ausschnitt">
                {ZEIG_GRENZE} von {treffer.length} Aufträgen, wartende zuerst, dann die aktuellsten.
                Suche und Filter greifen auf alle geladenen.
              </p>
            )}

            {amDeckel && (
              <p className={stile.hinweis} data-testid="deckel">
                Durchsucht werden die {HOL_GRENZE} zuletzt angelegten Aufträge. Ältere sind hier
                nicht auffindbar
                {gesamt
                  ? '.'
                  : ', ausgenommen Aufträge mit ausstehender Freigabe, die unabhängig vom Alter geladen werden.'}
              </p>
            )}

            {wartendeAmDeckel && (
              <p className={stile.hinweis} data-testid="deckel-wartende">
                Angezeigt werden {WARTENDE_GRENZE} Aufträge mit ausstehender Freigabe. Weitere
                werden hier nicht angezeigt.
              </p>
            )}

            {/* Tabelle ohne Kasten wie in der Content-Factory: eine Linie unter dem Kopf, die
                Zeilen liegen auf der Seitenflaeche, beim Zeigen wird die Zeile weiss. Die ganze
                Zeile oeffnet den Auftrag (Klickflaeche des Thema-Links); wartet ein Entwurf, fuehrt
                der Status direkt zur Freigabe. */}
            <div className={stile.tabellenrahmen}>
              <table className={stile.tabelle}>
                <thead>
                  <tr>
                    <th scope="col" className={stile.auswahlSpalte}>
                      <input
                        type="checkbox"
                        className={stile.checkbox}
                        aria-label="Alle angezeigten Aufträge auswählen"
                        checked={alleZeilenAusgewaehlt}
                        onChange={alleUmschalten}
                        // Waehrend ein Loeschen laeuft, ersetzt `bestaetigen` die Auswahl danach
                        // vollstaendig; eine zwischendurch geaenderte ginge sonst still verloren.
                        disabled={loescht || loeschbareZeilen.length === 0}
                      />
                    </th>
                    <th scope="col" className={stile.datum}>
                      Datum
                    </th>
                    <th scope="col" className={stile.typ}>
                      Typ
                    </th>
                    <th scope="col">Auftrag</th>
                    {gesamt && (
                      <th scope="col" className={stile.besitzer}>
                        Angelegt von
                      </th>
                    )}
                    <th scope="col" className={stile.status}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {zeilen.length === 0 && alle.length > 0 && (
                    <tr>
                      <td colSpan={gesamt ? 6 : 5} className={stile.keinTreffer}>
                        Kein Auftrag passt zu dieser Auswahl.
                      </td>
                    </tr>
                  )}
                  {zeilen.map((auftrag) => {
                    const gruppeDerZeile = gruppeVon(auftrag.status);
                    const themaId = `thema-${auftrag.runId}`;
                    // Zur Freigabe fuehrt der Status nur am eigenen Auftrag; einen fremden oeffnet
                    // die Zeile zum Lesen.
                    const pruefen = hatFreigabeSeite(auftrag) && !fremd(auftrag);
                    const text = statusText(anzeigeStatus(auftrag), auftrag.typ);
                    const inhalt = (
                      <>
                        <LinienSymbol name={STATUS_SYMBOL[gruppeDerZeile]} groesse={16} />
                        <span>{text}</span>
                      </>
                    );
                    return (
                      <tr
                        key={auftrag.runId}
                        className={stile.zeile}
                        data-hervorgehoben={gruppeDerZeile === 'wartet' ? 'true' : undefined}
                        data-ausgewaehlt={ausgewaehlt.has(auftrag.runId) ? 'true' : undefined}
                      >
                        <td className={stile.auswahlSpalte}>
                          <input
                            type="checkbox"
                            className={stile.checkbox}
                            aria-label={`Auftrag „${auftrag.thema}“ auswählen`}
                            checked={ausgewaehlt.has(auftrag.runId)}
                            onChange={(e) => auswahlUmschalten(auftrag.runId, e.target.checked)}
                            disabled={loescht || !loeschbarHier(auftrag)}
                            title={
                              fremd(auftrag)
                                ? 'Ausblenden kann nur die Person, die den Auftrag angelegt hat.'
                                : loeschbar(auftrag, jetzt)
                                  ? undefined
                                  : 'Ausblenden ist möglich, sobald der Auftrag abgeschlossen ist oder längere Zeit keinen neuen Stand gemeldet hat.'
                            }
                          />
                        </td>
                        <td className={stile.datum} title={alsDatumZeit(auftrag.angelegtAm)}>
                          {alsDatum(auftrag.angelegtAm)}
                        </td>
                        <td className={stile.typ}>
                          <span className={stile.typSymbol} title={TYP_TEXT[auftrag.typ]}>
                            <LinienSymbol name={AUFTRAGSTYPEN[auftrag.typ].symbol} groesse={16} />
                          </span>
                          <span className={seite.nurVorlesen}>{TYP_TEXT[auftrag.typ]}</span>
                        </td>
                        <td className={stile.thema}>
                          <Link
                            id={themaId}
                            className={stile.themaLink}
                            to={`/auftraege/${auftrag.runId}`}
                            title={auftrag.thema}
                          >
                            {auftrag.thema}
                          </Link>
                        </td>
                        {gesamt && (
                          <td className={stile.besitzer} title={auftrag.ownerEmail}>
                            {angelegtVon(auftrag, uid)}
                          </td>
                        )}
                        <td className={stile.status}>
                          {pruefen ? (
                            // Sichtbar der Status wie in der Content-Factory; der Name sagt
                            // zusaetzlich die Handlung. Die Beschreibung nennt den Gegenstand.
                            <Link
                              className={stile.statusLink}
                              data-gruppe={gruppeDerZeile}
                              to={`/auftraege/${auftrag.runId}/freigabe`}
                              aria-describedby={themaId}
                            >
                              {inhalt}
                              <span className={seite.nurVorlesen}>, Entwurf prüfen</span>
                            </Link>
                          ) : (
                            <span className={stile.statusText} data-gruppe={gruppeDerZeile}>
                              {inhalt}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}
